import { getXcoreProps, getXcoreServerProps } from "@robe/client/xcore-ui";
import { getXcoreConfig } from "@robe/core";
import { GetStaticPaths, GetStaticPathsContext, GetStaticPropsContext } from "next";
import { ComponentType, FC, ReactNode } from "react";
import { createCache } from "utils";
import { getLayout } from "./layout";

export * from "./layout";

export type RobePage = FC & {
  Layout?: ComponentType<{ children?: ReactNode }>;
  Subheader?: ComponentType;
};

const cachedLayout = createCache(getLayout, (ctx) => `layout-data/${ctx.locale ?? "default"}`);

/** `core` instance is initialized in `getXcoreProps` */
const cachedXcoreProps = getXcoreProps(cachedLayout);
export const getRobeServerSideProps = getXcoreServerProps(cachedLayout);

export const getRobeProps: typeof cachedXcoreProps =
  (...args) =>
    async (ctx: GetStaticPropsContext) =>
      await cachedXcoreProps(...args)(ctx);

/**
 * If news are defined in CTs to be pregenerated, pregenerate only those with `updatedAt` not older than last 30 days
 */
const NEWS_CONTENTS_SSG_FOR_LATEST_DAYS = 30;

export const getRobePaths =
  (contentType: string | string[]): GetStaticPaths =>
    async ({ locales = [] }: GetStaticPathsContext) => {
      const ssgContentTypes = resolveSSGPathsContentTypes();

      if (
        process.env.NO_SSG_PATHS === "1" ||
      locales.length === 0 ||
      ssgContentTypes.length === 0 ||
      // Skip static generation if content type is not in SSG_CONTENT_TYPES
      !ssgContentTypes.find((ct) => (Array.isArray(contentType) ? contentType.includes(ct) : contentType === ct))
      ) {
        return { paths: [], fallback: "blocking" };
      }

      const config = await getXcoreConfig();

      const paths = (
        await Promise.all(
          locales.map(async (locale) => {
            const data = await config.getCoreInstance(locale).content.listByTypes(Array.isArray(contentType) ? contentType : [contentType], {
              perPage: 10000,
              // Omit discontinued products and accessories
              locales: [locale],
              ...((contentType.includes("product") || contentType.includes("accessory")) && {
                fields: ["discontinuedGlob", "discontinuedReg"],
              }),
            });

            const oldestNewsContentDate = new Date(new Date().setDate(new Date().getDate() - NEWS_CONTENTS_SSG_FOR_LATEST_DAYS));

            const filteredContents = data.contents.filter((c) => {
              if (contentType === "news" && new Date(c.updatedAt) >= oldestNewsContentDate) {
                return true;
              }

              return !c.values?.discontinuedGlob && !c.values?.discontinuedReg;
            });

            return filteredContents.flatMap((c) =>
              c.publishedAt && typeof c.publishedAt === "object"
                ? Object.entries(c.publishedAt)
                  .filter(([, date]) => date)
                  .map(([cl]) => ({ locale: cl, params: { id: c.id.toString() } }))
                : []
            );
          })
        )
      ).flat();

      return {
      /**
       * New paths not returned by `getStaticPaths` will wait for the HTML to be generated,
       * identical to SSR (hence why `blocking`),
       * and then be cached for future requests so it only happens once per path.
       */
        fallback: "blocking",
        paths,
      };
    };

const resolveSSGPathsContentTypes = () => {
  const env = process.env.SSG_PATHS_CONTENT_TYPES;
  return env ? (env !== "" ? env.split(",") : []) : [];
};
